import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '@@common';

import { ContextEventHandlerClient, CreateContextEventHandlerRequest, UpdateContextEventHandlerRequest } from '../../../api/api';

export const eventHandlerQueryKeys = {
	all: (contextId: string) => ['contextEventHandlers', contextId] as const,
	available: (contextId: string) => ['availableContextEvents', contextId] as const,
};

export const useEventHandlerController = (contextId: string) => {
	const client = useApiClient(ContextEventHandlerClient);
	const queryClient = useQueryClient();

	const handlersQuery = useQuery({
		queryKey: eventHandlerQueryKeys.all(contextId),
		queryFn: () => client.getAllForContext(contextId),
		enabled: Boolean(contextId),
		staleTime: 30000,
	});

	const availableEventsQuery = useQuery({
		queryKey: eventHandlerQueryKeys.available(contextId),
		queryFn: () => client.getAvailableIntegrationEventsToHandle(contextId),
		enabled: Boolean(contextId),
		staleTime: 30000,
	});

	const createMutation = useMutation({
		mutationFn: (request: CreateContextEventHandlerRequest) => client.create(contextId, request),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: eventHandlerQueryKeys.all(contextId) }),
				queryClient.invalidateQueries({ queryKey: eventHandlerQueryKeys.available(contextId) }),
			]);
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({ eventHandlerId, request }: { eventHandlerId: string; request: UpdateContextEventHandlerRequest }) =>
			client.update(contextId, eventHandlerId, request),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: eventHandlerQueryKeys.all(contextId) });
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (eventHandlerId: string) => client.delete(contextId, eventHandlerId),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: eventHandlerQueryKeys.all(contextId) }),
				queryClient.invalidateQueries({ queryKey: eventHandlerQueryKeys.available(contextId) }),
			]);
		},
	});

	return {
		eventHandlers: handlersQuery.data ?? [],
		loadingEventHandlers: handlersQuery.isLoading,
		createEventHandler: (integrationEventId: string, name?: string, description?: string) =>
			createMutation.mutateAsync({ integrationEventId, name, description, tags: [] }),
		updateEventHandler: (eventHandlerId: string, request: UpdateContextEventHandlerRequest) =>
			updateMutation.mutateAsync({ eventHandlerId, request }),
		deleteEventHandler: (eventHandlerId: string) => deleteMutation.mutateAsync(eventHandlerId),
		availableEventsToHandle: availableEventsQuery.data ?? [],
		refreshEventHandlers: async () => {
			await handlersQuery.refetch();
		},
	};
};
